import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import Head from 'next/head';
import type { AppProps } from 'next/app';
import { ThemeProvider, StyledEngineProvider, Theme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { theme } from '@veeva-ctv/component-lib';
import Layout from '@/layout';
import { ApolloProvider } from '@apollo/client';
import client from '@/services/apollo';
import { init } from '@/utils/matomoUtils';
import { NextPage } from 'next';
import { TERMS_OF_SERVICE } from '@/queries';
import { URLsContext } from '@/context/URLsContext';
import { useRouter } from 'next/router';
import { MultiStepFromProvider } from '@/context/MultiStepFormContext';
import { TermsOfServiceQuery } from '@/gql/graphql';

declare module '@mui/styles' {
  // eslint-disable-next-line
  interface DefaultTheme extends Theme {}
}

// Make app config available globally
if (typeof window !== 'undefined') {
  window.ctv = {
    ENV_CTV: process.env.NEXT_PUBLIC_ENV_CTV,
    API_ENDPOINT: process.env.NEXT_PUBLIC_API_ENDPOINT,
    AUTH_ENDPOINT: process.env.NEXT_PUBLIC_AUTH_ENDPOINT,
    SHA: process.env.NEXT_PUBLIC_SHA,
  };
}

type Layout<P = object> = NextPage<P> & {
  layout?: string | undefined;
};

type Props = AppProps & {
  Component: Layout;
};

const MATOMO_ENABLED = process.env.NEXT_PUBLIC_MATOMO_ENABLED === 'true' ? true : false;
const MATOMO_URL = process.env.NEXT_PUBLIC_MATOMO_URL;
const MATOMO_TAG_MANAGER_URL = process.env.NEXT_PUBLIC_MATOMO_TAG_MANAGER_URL;
const MATOMO_SITE_ID = process.env.NEXT_PUBLIC_MATOMO_SITE_ID;

export default function MyApp({ Component, pageProps }: Props) {
  const router = useRouter();
  const [termsUrl, setTermsUrl] = useState<string | null | undefined>(
    process.env.NEXT_PUBLIC_TERMS_EXTERNAL_URL
  );

  const [learnMoreUrl, setLearnMoreUrl] = useState<string | null | undefined>(
    process.env.NEXT_PUBLIC_SITES_VEEVA_URL
  );

  // Remove server-side injected CSS.
  useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    jssStyles?.parentElement?.removeChild(jssStyles);
  }, []);

  // Matomo
  useEffect(() => {
    if (!router.isReady) return;
    setTimeout(() => {
      if (MATOMO_ENABLED && MATOMO_URL && MATOMO_SITE_ID) {
        init({
          url: MATOMO_URL,
          tagManagerUrl: MATOMO_TAG_MANAGER_URL,
          siteId: MATOMO_SITE_ID,
          manualPageViewTracking: true,
        });
      }
    });
  }, [router.isReady]);

  useEffect(() => {
    void client.query({ query: TERMS_OF_SERVICE }).then((res: { data: TermsOfServiceQuery }) => {
      if (res && res.data && res.data?.termsOfService && res.data?.termsOfService?.url) {
        setTermsUrl(res.data.termsOfService.url);
      }
    });
  }, []);

  useEffect(() => {
    if (!router.isReady || !router.query.code) return;
    if (router.query.code) {
      setLearnMoreUrl(process.env.NEXT_PUBLIC_SITEVAULT_STUDY_CONNECT_URL);
    }
  }, [router.isReady]);

  const layout = Component.layout;

  return (
    <>
      <Head>
        <title>SiteVault Signup</title>
        <meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width" />
      </Head>
      <Sentry.ErrorBoundary
        onError={(err) => {
          Sentry.captureException(err, {
            tags: {
              trigger: 'ErrorBoundary',
            },
          });
          throw err;
        }}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <ApolloProvider client={client}>
              <URLsContext.Provider value={{ LearnMoreHeaderURL: learnMoreUrl, TOSUrl: termsUrl }}>
                <MultiStepFromProvider>
                  <Layout layout={layout}>
                    <Component {...pageProps} />
                  </Layout>
                </MultiStepFromProvider>
              </URLsContext.Provider>
            </ApolloProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </Sentry.ErrorBoundary>
    </>
  );
}

MyApp.propTypes = {
  Component: PropTypes.elementType.isRequired,
  pageProps: PropTypes.object.isRequired,
};
